import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Timesheet } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextAreaField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useMemberOptions } from '../time-entries/api.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { STATUS_LABELS, STATUS_TONES } from './status.js';
import { DueIndicator } from './DueIndicator.js';
import {
  useApproveTimesheet,
  useApproveTimesheetsBatch,
  useRejectTimesheet,
  useRejectTimesheetsBatch,
  useTimesheets,
} from './api.js';

/** What the reject modal is acting on: a single timesheet or the current selection. */
type RejectTarget = { kind: 'single'; ts: Timesheet } | { kind: 'bulk'; ids: string[] };

/** Compact “Johan → Jeff” chain cell; done steps green, the awaiting step highlighted. */
function ApprovalChainCell({ ts }: { ts: Timesheet }): JSX.Element {
  if (ts.approvalSteps.length === 0) return <span className="text-slate-400">—</span>;
  const currentId =
    ts.status === 'submitted'
      ? [...ts.approvalSteps]
          .filter((s) => s.status === 'pending')
          .sort((a, b) => a.sortOrder - b.sortOrder)[0]?.id
      : undefined;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5 whitespace-nowrap">
      {ts.approvalSteps.map((step, index) => {
        const tone =
          step.status === 'approved'
            ? 'text-emerald-700'
            : step.status === 'rejected'
              ? 'text-rose-700'
              : step.id === currentId
                ? 'font-semibold text-sky-700'
                : 'text-slate-400';
        const suffix =
          step.status === 'approved' ? ' ✓' : step.status === 'rejected' ? ' ✕' : '';
        return (
          <span key={step.id} className="inline-flex items-center gap-1">
            {index > 0 ? <span className="text-slate-300">→</span> : null}
            <span
              className={tone}
              title={
                step.status === 'pending' && step.id === currentId
                  ? `Awaiting ${step.approverName}`
                  : `${step.approverName} — ${step.status}${
                      step.decidedAt ? ` ${new Date(step.decidedAt).toLocaleString()}` : ''
                    }`
              }
            >
              {step.approverName}
              {suffix}
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function ApprovalsPage(): JSX.Element {
  const { principal } = useAuth();
  const navigate = useNavigate();
  const currentMemberId = principal?.type === 'member' ? principal.id : '';

  const [page, setPage] = useState(1);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
  const [reason, setReason] = useState('');

  // Default the approver filter to the logged-in member once the principal resolves.
  // A saved filter default (below) loads afterwards and overrides this seed.
  const didInitApprover = useRef(false);
  useEffect(() => {
    if (didInitApprover.current || !principal) {
      return;
    }
    didInitApprover.current = true;
    if (currentMemberId) {
      setApproverIds([currentMemberId]);
    }
  }, [principal, currentMemberId]);

  const filterDefaults = useFilterDefaults('approvals', (saved) => {
    didInitApprover.current = true;
    const savedMembers = savedStringArray(saved, 'memberIds');
    if (savedMembers !== null) setMemberIds(savedMembers);
    const savedApprovers = savedStringArray(saved, 'approverIds');
    if (savedApprovers !== null) setApproverIds(savedApprovers);
  });

  const query = useTimesheets({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: 'submitted',
    memberIds: memberIds.length > 0 ? memberIds : undefined,
    approverIds: approverIds.length > 0 ? approverIds : undefined,
    sortBy: 'weekStartDate',
    sortDir: 'asc',
  });
  const membersQuery = useMemberOptions();
  const approveMutation = useApproveTimesheet();
  const rejectMutation = useRejectTimesheet();
  const approveBatchMutation = useApproveTimesheetsBatch();
  const rejectBatchMutation = useRejectTimesheetsBatch();

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) {
      map.set(m.id, m.displayName);
    }
    return map;
  }, [membersQuery.data]);

  const timesheets = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const pageIds = timesheets.map((ts) => ts.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const selectedCount = selected.size;
  const busy =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    approveBatchMutation.isPending ||
    rejectBatchMutation.isPending;

  // Filters and page changes act on a fresh selection so hidden rows are never affected.
  const clearSelection = (): void => setSelected(new Set());
  const changePage = (next: number): void => {
    clearSelection();
    setPage(next);
  };

  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(pageIds));
  };

  const toggleOne = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openSingleReject = (ts: Timesheet): void => {
    setRejectTarget({ kind: 'single', ts });
    setReason('');
    rejectMutation.reset();
    rejectBatchMutation.reset();
  };

  const openBulkReject = (): void => {
    if (selectedCount === 0) {
      return;
    }
    setRejectTarget({ kind: 'bulk', ids: [...selected] });
    setReason('');
    rejectMutation.reset();
    rejectBatchMutation.reset();
  };

  const submitReject = (): void => {
    if (!rejectTarget || !reason.trim()) {
      return;
    }
    if (rejectTarget.kind === 'single') {
      rejectMutation.mutate(
        { id: rejectTarget.ts.id, input: { reason: reason.trim() } },
        { onSuccess: () => setRejectTarget(null) },
      );
    } else {
      rejectBatchMutation.mutate(
        { ids: rejectTarget.ids, reason: reason.trim() },
        {
          onSuccess: () => {
            clearSelection();
            setRejectTarget(null);
          },
        },
      );
    }
  };

  const approveSelected = (): void => {
    if (selectedCount === 0) {
      return;
    }
    approveBatchMutation.mutate({ ids: [...selected] }, { onSuccess: clearSelection });
  };

  const rejectModalError =
    rejectMutation.isError || rejectBatchMutation.isError
      ? 'Failed to un-submit the timesheet(s). Try again.'
      : undefined;

  return (
    <div>
      <PageHeader
        title="Timesheet Approvals"
        description="Review submitted weekly timesheets and approve or un-submit them."
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <MemberMultiFilter
            selected={memberIds}
            onChange={(next) => {
              clearSelection();
              setPage(1);
              setMemberIds(next);
            }}
            allLabel="All members"
          />
          <MemberMultiFilter
            selected={approverIds}
            onChange={(next) => {
              clearSelection();
              setPage(1);
              setApproverIds(next);
            }}
            allLabel="All approvers"
            restrict={(m) => m.isTimeApprover}
          />
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() => filterDefaults.save({ memberIds, approverIds })}
          />

          {selectedCount > 0 ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-slate-500">{selectedCount} selected</span>
              <Button variant="secondary" onClick={openBulkReject} disabled={busy}>
                Un-Submit selected
              </Button>
              <Button
                onClick={approveSelected}
                isLoading={approveBatchMutation.isPending}
                disabled={busy && !approveBatchMutation.isPending}
              >
                Approve selected
              </Button>
            </div>
          ) : null}
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all timesheets on this page"
                />
              </th>
              <th className="px-4 py-3 font-medium">Week of</th>
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Approval chain</th>
              <th className="px-4 py-3 font-medium">Due</th>
              <th className="px-4 py-3 font-medium text-right">Hours</th>
              <th className="px-4 py-3 font-medium text-right">Billable</th>
              <th className="px-4 py-3 font-medium text-right">Entries</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={10}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={timesheets.length === 0}
              emptyTitle="Nothing to approve"
              emptyDescription="Submitted timesheets awaiting approval will appear here."
            >
              {timesheets.map((ts) => (
                <tr
                  key={ts.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/approvals/${ts.id}`)}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      checked={selected.has(ts.id)}
                      onChange={() => toggleOne(ts.id)}
                      aria-label={`Select timesheet for ${memberNames.get(ts.memberId) ?? 'member'}`}
                    />
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-900">
                    {formatDate(ts.weekStartDate)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {memberNames.get(ts.memberId) ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONES[ts.status]}>{STATUS_LABELS[ts.status]}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <ApprovalChainCell ts={ts} />
                  </td>
                  <td className="px-4 py-3">
                    <DueIndicator timesheet={ts} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                    {ts.totalHours}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {ts.billableHours}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {ts.entryCount}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="secondary"
                        onClick={() => openSingleReject(ts)}
                        disabled={busy}
                      >
                        Un-Submit
                      </Button>
                      <Button
                        onClick={() => approveMutation.mutate(ts.id)}
                        isLoading={approveMutation.isPending && approveMutation.variables === ts.id}
                        disabled={busy && approveMutation.variables !== ts.id}
                      >
                        Approve
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && timesheets.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={changePage}
          />
        ) : null}
      </div>

      <Modal
        isOpen={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        title={rejectTarget?.kind === 'bulk' ? 'Un-Submit timesheets' : 'Un-Submit timesheet'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRejectTarget(null)}
              disabled={rejectMutation.isPending || rejectBatchMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitReject}
              isLoading={rejectMutation.isPending || rejectBatchMutation.isPending}
              disabled={!reason.trim()}
            >
              Un-Submit
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-slate-600">
            {rejectTarget?.kind === 'bulk'
              ? `Provide a reason for un-submitting ${rejectTarget.ids.length} timesheet(s). Members can revise and resubmit.`
              : 'Provide a reason for un-submitting this timesheet. The member can revise and resubmit.'}
          </p>
          <TextAreaField
            label="Reason"
            required
            rows={3}
            value={reason}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
            error={rejectModalError}
          />
        </div>
      </Modal>
      <DetailOverlay listPath="/approvals" />
    </div>
  );
}
